import { Injectable, OnDestroy } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { UserService } from '@app/admin-station/work-center/user/services/user.service';
import { AuthenticationResult } from '@azure/msal-common';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { SESSION_ORG_ID, USER_DB_PREFERENCES } from '@app/shared/constants/localstorage-keys';
import { BrowserAuthError, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { ConfigurationService } from '../utils/configuration.service';
import { IAction } from '@app/shared/interfaces/action.interface';
import { MixpanelService } from '../mixpanel/mixpanel.service';
import { mixpanelEventTypes } from '@app/shared/enums/mixpanel.enum';
import { EPermissionKeys } from '@app/shared/enums/permission-keys';

export function msalReady(
  msalBroadcastService: MsalBroadcastService,
  msalService: MsalService
): Observable<void> {
  return msalBroadcastService.inProgress$.pipe(
    filter(
      (status) =>
        status === InteractionStatus.None || status === InteractionStatus.Startup
    ),
    take(1),
    switchMap((status) => {
      if (status === InteractionStatus.Startup) {
        // Call handleRedirectObservable to help MSAL transition from Startup
        return msalService.handleRedirectObservable().pipe(
          tap(() => {
            // After redirect is handled, MSAL will transition to None
          }),
          switchMap(() => of(void 0))
        );
      }
      // Already ready
      return of(void 0);
    })
  );
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private _dbLocalStorage?: any[];
  private _loginTime: Date;

  private _isUserAuthenticated = false;
  private _isUserAuthenticated$ = new BehaviorSubject<boolean>(false);
  private readonly _destroying$ = new Subject<void>();

  // listen to this to check if the user has been authenticated by msal, emits on every reload
  public isUserAuthenticated$ = this._isUserAuthenticated$.asObservable();
  public bearerTokenSet = false;

  constructor(
    private configService: ConfigurationService,
    private mixpanelService: MixpanelService,
    private msalService: MsalService,
    private _msalBroadcastService: MsalBroadcastService,
    private userService: UserService
  ) {
    /**
     * Subscribes to msal inProgress$ to monitor msal interactions.
     * When an interaction is over, it sets the login status and the active account
     */
    this._msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this._setActiveAccount();
        this._setLoginStatus();
      });

    /**
     * Subscribes to msal msalSubject$ for confirmation of login success.
     * It then tracks the mixpanel login event and logs the login time.
     */
    this._msalBroadcastService.msalSubject$
      .pipe(
        filter((event: EventMessage) => event.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this._logLoginTime();
        this.mixpanelService.trackEvent(mixpanelEventTypes.USER_LOGIN);
      });

    // TODO_MAT remove this from here
    const dbLocalStorage = this.userService.getUserDBPreferences(USER_DB_PREFERENCES);
    if (dbLocalStorage && this._dbLocalStorage === undefined) {
      this._dbLocalStorage = dbLocalStorage;
    }

    /**
     * this handler will listen to a logout event fired from any other window or tab on the same host,
     * and logout from all sessions.
     */
    const logoutEvent = new BroadcastChannel('logoutChannel');
    logoutEvent.onmessage = (event) => {
      if (event.data === 'logout') {
        if (this._loginTime) this._logElapsedSessionTime();

        this.msalService
          .handleRedirectObservable()
          .pipe(takeUntil(this._destroying$))
          .subscribe({
            next: () => {
              this.msalService.logoutRedirect();
            },
            error: (error) => console.error(error),
          });
      }
    };
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Use this to get the API access token.
   * This is used by the angular HTTPClient interceptor (when using the data service to query the API),
   * by the dev extreme API and by the SignalR API.
   * This token must be added as a header bearer token on each request.
   */
  public acquireToken = (): Observable<AuthenticationResult> => {
    var silentRequest = {
      scopes: this.configService.getMsalSettings('guard').authRequest.scopes,
      account: this.msalService.instance.getActiveAccount(),
      forceRefresh: false,
    };
    return this.msalService.acquireTokenSilent(silentRequest).pipe(
      map((res: AuthenticationResult) => {
        return res;
      }),
      catchError((error: BrowserAuthError) => {
        if (error.errorCode === 'no_account_error' || error.errorCode === 'login_required') {
          // Wait for any in-progress interaction to finish before starting a new one,
          // otherwise a stale interaction lock causes B2C to return 400 Bad Request.
          // prompt:'login' forces B2C to show a fresh login form instead of attempting
          // SSO against a potentially stale/corrupt server-side B2C session.
          msalReady(this._msalBroadcastService, this.msalService).subscribe(() => {
            this.msalService.loginRedirect({ scopes: [], prompt: 'login' });
          });
        } else {
          this.logout();
        }
        return throwError(() => error);
      })
    );
  };

  /**
   * Performs login using MSAL service.
   * If the user doesn't have a valid account,
   * it will redirect to the login page
   *  */
  public login(): void {
    msalReady(this._msalBroadcastService, this.msalService).subscribe(() => {
      // Use getAllAccounts() directly — _isUserAuthenticated may still be false
      // when msalReady resolves via the Startup branch (handleRedirectObservable
      // just ran and populated accounts, but the inProgress$ → None event that
      // drives _setLoginStatus hasn't fired yet). Checking the flag here would
      // trigger a second loginRedirect immediately after B2C returned, causing
      // a 400 Bad Request on the SelfAsserted endpoint.
      if (this.msalService.instance.getAllAccounts().length === 0) {
        this.msalService.loginRedirect({ scopes: [], prompt: 'login' });
      }
      const data = this.msalService.instance.getActiveAccount();
      this.userService.setActiveUserAccount(data?.username);
    });
  }

  /**
   * Logs out from msal and then broadcasts 'logout' message
   * for other tabs to logout as well.
   */
  public logout(): void {
    msalReady(this._msalBroadcastService, this.msalService).subscribe(() => {
      this.userService.clearUserDataCache();
      this.msalService.logoutRedirect();

      // notify all CMCLoud open windows/tabs to perform a logout
      const logoutEvent = new BroadcastChannel('logoutChannel');
      logoutEvent.postMessage('logout');
    });
  }

  private _logElapsedSessionTime(): void {
    const logoutTime = new Date();
    const sessionDuration = (logoutTime.getTime() - this._loginTime.getTime()) / 1000;
    this.mixpanelService.trackEvent(mixpanelEventTypes.USER_LOGOUT, {
      sessionDurationSecs: Math.round(sessionDuration),
    });
  }

  private _logLoginTime(): void {
    this._loginTime = new Date();
  }

  /**
   * Sets the active account in case you are authenticated on msal from multiple apps.
   * This is the account that will be used for token requests.
   */
  private _setActiveAccount(): void {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      activeAccount = this.msalService.instance.getAllAccounts()[0];
      this.msalService.instance.setActiveAccount(activeAccount);
    }
  }

  /**
   * sets the user authentication status and emits isUserAuthenticated$.
   * This should be used to check if the user is authenticated to msal.
   */
  private _setLoginStatus(): void {
    this._isUserAuthenticated = this.msalService.instance.getAllAccounts().length > 0;
    this._isUserAuthenticated$.next(this._isUserAuthenticated);
  }

  // TODO: remove all this ----------------------------------
  public getUserRolesFromCache(): any[] {
    return this.userService.getUserLoginData()?.roles;
  }

  public getUserPermissionsFromCache(): any[] {
    return this.userService.getUserLoginData()?.permissions;
  }

  public getOrgPermissionsFromCache(): string[] {
    return this.userService.getUserLoginData()?.orgPermissions;
  }

  public getOrgAccessFromCache(): string[] {
    return this.userService.getUserLoginData()?.orgAccess;
  }

  public getUserRootOrgIdFromCache(): string {
    return this.userService.getUserLoginData()?.rootOrgId;
  }

  public doIHaveAccess<T extends { orgId: string }>(obj: T): boolean {
    return this.hasAccessTo(obj, (obj) => obj.orgId);
  }

  public isSharedWithMe<T extends { partnerId: string }>(obj: T): boolean {
    return this.hasAccessTo(obj, (obj) => obj.partnerId);
  }

  private hasAccessTo<T>(obj: T, getProperty: (obj: T) => string): boolean {
    const orgAccess = this.getOrgAccessFromCache();
    return orgAccess.includes(getProperty(obj));
  }

  /**
   * Check if the user has the specified action permission.
   * @param key - The permission key to be verified.
   * @returns True if the user has the specified action permission, false otherwise.
   * If the user is an owner or admin for the organization, this function will return true if the user has the specified permission
   * on the organization level. Otherwise, it will check if the user has the specified permission on the user level.
   */
  public haveActionPermission(key: string): boolean {
    const userData = this.userService.getUserLoginData();
    if (!userData) return false;

    const isOwner = userData.isOwner;
    const isAdmin = userData.isAdmin;
    const permissions = userData.permissions;

    if (isOwner || isAdmin) return this.haveOrgActionPermission(key);

    return this.haveOrgActionPermission(key) && permissions.includes(key);
  }

  public haveOrgActionPermission(key: string): boolean {
    const permissions = this.getOrgPermissionsFromCache();
    return permissions?.includes(key);
  }

  public validateOptionsPermissions(options: IAction[]) {
    return options?.map((item) => {
      item.disabled = item?.permissionKey ? !this.haveActionPermission(item?.permissionKey) : false;
      return item;
    });
  }

  public getUserID(): string {
    return this.userService.getUserLoginData()?.userId;
  }

  public getOrgID(): string {
    return this.userService.getUserLoginData()?.orgId;
  }

  get sessionOrgId(): string {
    let result = this.userService.getSessionOrgId(SESSION_ORG_ID);
    if (!result) {
      result = this.getOrgID();
    }
    return result;
  }
  set sessionOrgId(id: string) {
    this.userService.setSessionOrgId(SESSION_ORG_ID, id);
  }

  public haveAtLeastOnePermission(options: any[]) {
    return options?.some((opt) => {
      return this.haveActionPermission(opt.permissionKey);
    });
  }

  getDbLocalStorageKey(key: string) {
    if (this._dbLocalStorage) {
      if (Array.isArray(this._dbLocalStorage)) {
        return this._dbLocalStorage.find((item) => item.key === key);
      }

      return this._dbLocalStorage;
    }
  }

  set dbLocalStorage(localStorage) {
    this.userService.setUserDBPreferences(USER_DB_PREFERENCES, localStorage);
  }
  // ----------------------------------------------------
}
